import type { PromptIcon } from '@/lib/aiScreenPrompts'

// ── What the copilot can do, per role ───────────────────────────────────────
// The empty chat lists it so staff know the copilot does more than answer
// questions: it fetches live figures, prepares menu changes, opens screens
// and gives how-to steps. The role groups mirror the tools each role gets in
// backend/internal/aiagent/manifest.go (reportRoles, storeRoles, counterRoles,
// kitchenRoles) and menuWriteRoles in handlers/ai_write_menu.go — change
// them together. Display only: the backend decides what each role may call.

export interface CopilotCapability {
  id: string
  icon: PromptIcon
  title: string
  /** Seeded into the composer when the chip is tapped. */
  example: string
}

const REPORT_ROLES = ['admin', 'manager', 'accountant']
const STORE_ROLES = ['admin', 'inventory_manager']
const COUNTER_ROLES = ['counter', 'manager', 'admin']
const KITCHEN_ROLES = ['kitchen', 'admin', 'kitchen_manager']
const MENU_WRITE_ROLES = ['admin', 'manager']

const NUMBERS_TITLE: Record<string, string> = {
  admin: 'Sales, profit & expenses',
  accountant: 'Sales, tax & expenses',
}

const HOW_TO_EXAMPLE: Record<string, string> = {
  counter: 'How do I split a bill?',
  server: 'How do I fire a KOT?',
  kitchen: 'How do I recall a bumped ticket?',
  kitchen_manager: 'How do I recall a bumped ticket?',
  inventory_manager: 'How do I do a stock count?',
  accountant: 'Where do I see the profit and loss?',
}

/**
 * The capabilities to show a role. `flags` is ai_nav_flags (no `pos` = the
 * marketing edition, where no POS data tool runs); `writes` is ai_writes.
 */
export function copilotCapabilities(role: string, opts: { flags: string[]; writes: boolean }): CopilotCapability[] {
  const pos = opts.flags.includes('pos')
  const out: CopilotCapability[] = []
  if (pos && REPORT_ROLES.includes(role)) {
    out.push({
      id: 'numbers',
      icon: 'chart',
      title: NUMBERS_TITLE[role] ?? 'Sales, best sellers & expenses',
      example: role === 'admin' ? 'Compare this week with last week' : 'How are sales today?',
    })
  }
  if (pos && opts.writes && MENU_WRITE_ROLES.includes(role)) {
    out.push({ id: 'menu', icon: 'edit', title: 'Menu changes — you confirm', example: 'Raise the price of ' })
  }
  if (pos && STORE_ROLES.includes(role)) {
    out.push({ id: 'stock', icon: 'stock', title: 'Low stock & vendors', example: 'Which items are low on stock, and who supplies them?' })
  }
  if (pos && COUNTER_ROLES.includes(role)) {
    out.push({ id: 'floor', icon: 'users', title: 'Open orders & free tables', example: 'Which tables are free?' })
  }
  if (pos && KITCHEN_ROLES.includes(role)) {
    out.push({ id: 'kitchen', icon: 'receipt', title: 'Recipes & the kitchen queue', example: "What's in the queue?" })
  }
  out.push({
    id: 'howto',
    icon: 'help',
    title: 'How-to steps — opens the screen',
    example: HOW_TO_EXAMPLE[role] ?? 'How do I close the day?',
  })
  return out
}
